'use client';
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';

export default function LoginPage() {
  const router = useRouter();
  const { user, loading, signIn, signUp } = useAuth();
  const [tab, setTab] = useState<'login' | 'recuperar' | 'registro'>('login');
  const [email, setEmail] = useState('');
  const [contrasena, setContrasena] = useState('');
  const [mostrarContrasena, setMostrarContrasena] = useState(false);
  const [recordarme, setRecordarme] = useState(false);
  const [error, setError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [correoRecuperar, setCorreoRecuperar] = useState('');
  const [mensajeRecuperar, setMensajeRecuperar] = useState('');
  const [recuperarLoading, setRecuperarLoading] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Registro state
  const [regNombre, setRegNombre] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regContrasena, setRegContrasena] = useState('');
  const [regConfirmar, setRegConfirmar] = useState('');
  const [mostrarRegContrasena, setMostrarRegContrasena] = useState(false);
  const [mostrarRegConfirmar, setMostrarRegConfirmar] = useState(false);
  const [regLoading, setRegLoading] = useState(false);
  const [regError, setRegError] = useState('');
  const [regErrorType, setRegErrorType] = useState<'existing' | 'general' | ''>('');
  const [regSuccess, setRegSuccess] = useState('');
  const [regErrors, setRegErrors] = useState<{ nombre?: string; email?: string; contrasena?: string; confirmar?: string }>({});

  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    if (!loading && user) {
      router.replace('/');
    }
  }, [user, loading, router]);

  function switchTab(newTab: 'login' | 'recuperar' | 'registro') {
    setTab(newTab);
    setError('');
    setRegError('');
    setRegErrorType('');
    setRegSuccess('');
    setRegErrors({});
    setMensajeRecuperar('');
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoginLoading(true);
    try {
      await signIn(email, contrasena);
      router.replace('/');
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message || '';
      if (msg.includes('Invalid login credentials') || msg.includes('invalid_credentials')) {
        setError('Usuario o contraseña incorrectos.');
      } else if (msg.includes('Email not confirmed')) {
        setError('Por favor confirma tu correo electrónico.');
      } else if (msg.includes('Too many requests')) {
        setError('Demasiados intentos. Por favor espera unos minutos.');
      } else {
        setError(msg || 'Error al iniciar sesión. Intenta nuevamente.');
      }
    } finally {
      setLoginLoading(false);
    }
  }

  async function handleRecuperar(e: React.FormEvent) {
    e.preventDefault();
    setMensajeRecuperar('');
    setRecuperarLoading(true);
    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(correoRecuperar, {
        redirectTo: `${window.location.origin}/auth/callback?next=/reset-password`,
      });
      if (resetError) {
        setMensajeRecuperar('Si el correo existe en el sistema, recibirás instrucciones de recuperación.');
      } else {
        setMensajeRecuperar('Correo de recuperación enviado. Revisa tu bandeja de entrada.');
      }
    } catch {
      setMensajeRecuperar('Si el correo existe en el sistema, recibirás instrucciones de recuperación.');
    } finally {
      setRecuperarLoading(false);
    }
  }

  function validateRegistro(): boolean {
    const errs: { nombre?: string; email?: string; contrasena?: string; confirmar?: string } = {};
    if (!regNombre.trim()) errs.nombre = 'Este campo es obligatorio.';
    if (!regEmail.trim()) {
      errs.email = 'Este campo es obligatorio.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(regEmail)) {
      errs.email = 'Ingresa un correo válido.';
    }
    if (!regContrasena) {
      errs.contrasena = 'Este campo es obligatorio.';
    } else if (regContrasena.length < 6) {
      errs.contrasena = 'La contraseña debe tener al menos 6 caracteres.';
    }
    if (!regConfirmar) {
      errs.confirmar = 'Este campo es obligatorio.';
    } else if (regContrasena !== regConfirmar) {
      errs.confirmar = 'Las contraseñas no coinciden.';
    }
    setRegErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleRegistro(e: React.FormEvent) {
    e.preventDefault();
    setRegError('');
    setRegErrorType('');
    setRegSuccess('');
    if (!validateRegistro()) return;
    setRegLoading(true);
    try {
      await signUp(regEmail, regContrasena, { fullName: regNombre.trim() });
      setRegSuccess('email_confirmation');
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message || '';
      if (
        msg.includes('User already registered') ||
        msg.includes('already registered') ||
        msg.includes('already exists') ||
        msg.includes('email address is already')
      ) {
        setRegErrorType('existing');
      } else {
        setRegError(msg || 'Error al crear la cuenta. Intenta nuevamente.');
        setRegErrorType('general');
      }
    } finally {
      setRegLoading(false);
    }
  }

  if (!mounted || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
              <span className="text-primary font-bold text-lg">E</span>
            </div>
            <div className="absolute inset-0 rounded-2xl border-2 border-primary/30 animate-ping" />
          </div>
          <p className="text-xs text-muted-foreground tracking-widest uppercase">Cargando...</p>
        </div>
      </div>
    );
  }

  const brandingPanel = (
    <div
      className="hidden lg:flex lg:w-[45%] xl:w-[42%] flex-col justify-between p-10 relative overflow-hidden"
      style={{ background: 'linear-gradient(145deg, #0a0906 0%, #111008 50%, #0f0d06 100%)' }}
    >
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(212,175,55,1) 1px, transparent 0)', backgroundSize: '28px 28px' }} />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full opacity-[0.08]" style={{ background: 'radial-gradient(circle, #D4AF37, transparent 70%)' }} />
      <div className="absolute bottom-0 right-0 w-64 h-64 rounded-full opacity-[0.05]" style={{ background: 'radial-gradient(circle, #B8960C, transparent 70%)', transform: 'translate(30%, 30%)' }} />

      <div className="relative z-10">
        <div className="flex items-center gap-3 mb-12">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #B8960C, #D4AF37)', boxShadow: '0 4px 16px rgba(184,150,12,0.4)' }}>
            <span className="text-black font-bold text-base">E</span>
          </div>
          <div>
            <p className="text-white font-bold text-base leading-tight tracking-tight">EGA Studio</p>
            <p className="text-[11px] font-600 tracking-[0.15em] uppercase" style={{ color: '#D4AF37' }}>Manager PRO</p>
          </div>
        </div>
        <div className="space-y-3">
          <h2 className="text-3xl font-800 text-white leading-tight tracking-tight">
            Gestión audiovisual
          </h2>
          <h2 className="text-3xl font-800 leading-tight tracking-tight" style={{ background: 'linear-gradient(135deg, #B8960C, #D4AF37, #F0D060)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            profesional
          </h2>
          <p className="text-sm leading-relaxed pt-1" style={{ color: 'rgba(255,255,255,0.45)' }}>
            Administra clientes, cotizaciones, contratos, pagos y producción desde un solo lugar.
          </p>
        </div>
      </div>

      <div className="relative z-10 space-y-3">
        {[
          { icon: '⚡', text: 'Sincronización en tiempo real' },
          { icon: '🔒', text: 'Seguridad con Supabase Auth' },
          { icon: '📊', text: 'Dashboard financiero completo' },
          { icon: '🎬', text: 'Gestión de producción audiovisual' },
        ].map((f) => (
          <div key={f.text} className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center text-sm flex-shrink-0" style={{ background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.2)' }}>
              {f.icon}
            </div>
            <span className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>{f.text}</span>
          </div>
        ))}
      </div>

      <div className="relative z-10">
        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>© 2026 EGA Studio Manager PRO</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex">
      {brandingPanel}

      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 bg-background">
        <div className="w-full max-w-[400px]">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #B8960C, #D4AF37)', boxShadow: '0 4px 12px rgba(184,150,12,0.3)' }}>
              <span className="text-black font-bold text-sm">E</span>
            </div>
            <div>
              <p className="font-bold text-sm text-foreground leading-tight">EGA Studio</p>
              <p className="text-2xs font-600 tracking-[0.12em] uppercase text-primary">Manager PRO</p>
            </div>
          </div>

          {/* Header text */}
          <div className="mb-7">
            <h1 className="text-2xl font-800 text-foreground tracking-tight">
              {tab === 'login' ? 'Bienvenido de vuelta' : tab === 'registro' ? 'Crear cuenta' : 'Recuperar contraseña'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {tab === 'login' ?'Ingresa tus credenciales para continuar'
                : tab === 'registro' ?'Crea tu cuenta de EGA Studio Manager PRO' :'Te enviaremos un enlace de recuperación'}
            </p>
          </div>

          <div className="bg-card border border-border rounded-2xl p-6" style={{ boxShadow: 'var(--shadow-card-hover)' }}>

            {/* ── LOGIN TAB ── */}
            {tab === 'login' && (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="text-xs font-600 text-foreground block mb-1.5">Correo Electrónico</label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    placeholder="admin@egastudio.hn"
                    className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 transition-all"
                  />
                </div>

                <div>
                  <label className="text-xs font-600 text-foreground block mb-1.5">Contraseña</label>
                  <div className="relative">
                    <input
                      type={mostrarContrasena ? 'text' : 'password'}
                      value={contrasena}
                      onChange={e => setContrasena(e.target.value)}
                      required
                      placeholder="••••••••"
                      className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 transition-all pr-11"
                    />
                    <button
                      type="button"
                      onClick={() => setMostrarContrasena(!mostrarContrasena)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-0.5"
                    >
                      {mostrarContrasena ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                          <line x1="1" y1="1" x2="23" y2="23"/>
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={recordarme}
                        onChange={e => setRecordarme(e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${recordarme ? 'bg-primary border-primary' : 'border-border bg-input group-hover:border-primary/50'}`}>
                        {recordarme && (
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">Recordarme</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => switchTab('recuperar')}
                    className="text-xs text-primary hover:text-accent transition-colors font-500"
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>

                {error && (
                  <div className="flex items-start gap-2.5 bg-negative/8 border border-negative/20 rounded-xl px-4 py-3">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-negative flex-shrink-0 mt-0.5">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <span className="text-sm text-negative leading-tight">{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loginLoading}
                  className="w-full py-2.5 rounded-xl text-sm font-700 text-black transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: 'linear-gradient(135deg, #B8960C, #D4AF37)', boxShadow: loginLoading ? 'none' : '0 4px 14px rgba(184,150,12,0.35)' }}
                >
                  {loginLoading ? (
                    <>
                      <svg className="animate-spin" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                      </svg>
                      Verificando...
                    </>
                  ) : (
                    <>
                      Iniciar sesión
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="5" y1="12" x2="19" y2="12"/>
                        <polyline points="12 5 19 12 12 19"/>
                      </svg>
                    </>
                  )}
                </button>

                {/* Crear cuenta link */}
                <div className="pt-1 border-t border-border/50">
                  <p className="text-center text-xs text-muted-foreground pt-3">
                    ¿No tienes una cuenta?
                  </p>
                  <button
                    type="button"
                    onClick={() => switchTab('registro')}
                    className="w-full mt-2 py-2.5 rounded-xl text-sm font-600 border transition-all duration-200 flex items-center justify-center gap-2"
                    style={{ borderColor: 'rgba(212,175,55,0.4)', color: '#D4AF37', background: 'rgba(212,175,55,0.06)' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(212,175,55,0.12)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(212,175,55,0.7)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(212,175,55,0.06)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(212,175,55,0.4)'; }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <line x1="19" y1="8" x2="19" y2="14"/>
                      <line x1="22" y1="11" x2="16" y2="11"/>
                    </svg>
                    Crear cuenta
                  </button>
                </div>
              </form>
            )}

            {/* ── REGISTRO TAB ── */}
            {tab === 'registro' && (
              <form onSubmit={handleRegistro} className="space-y-4" noValidate>
                {/* Success state */}
                {regSuccess === 'email_confirmation' && (
                  <div className="flex flex-col items-center gap-3 py-4 text-center">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)' }}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-700 text-foreground">¡Cuenta creada correctamente!</p>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        Revisa tu correo electrónico para confirmar tu cuenta antes de iniciar sesión.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => switchTab('login')}
                      className="mt-2 w-full py-2.5 rounded-xl text-sm font-700 text-black transition-all duration-200 flex items-center justify-center gap-2"
                      style={{ background: 'linear-gradient(135deg, #B8960C, #D4AF37)', boxShadow: '0 4px 14px rgba(184,150,12,0.35)' }}
                    >
                      Iniciar sesión
                    </button>
                  </div>
                )}

                {/* Existing account error */}
                {regErrorType === 'existing' && (
                  <div className="rounded-xl border px-4 py-4 space-y-3" style={{ background: 'rgba(245,158,11,0.06)', borderColor: 'rgba(245,158,11,0.3)' }}>
                    <div className="flex items-start gap-2.5">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                        <line x1="12" y1="9" x2="12" y2="13"/>
                        <line x1="12" y1="17" x2="12.01" y2="17"/>
                      </svg>
                      <div>
                        <p className="text-sm font-600" style={{ color: '#f59e0b' }}>Esta cuenta ya existe</p>
                        <p className="text-xs text-muted-foreground mt-0.5">Intenta iniciar sesión o recuperar tu contraseña.</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => switchTab('login')}
                        className="flex-1 py-2 rounded-lg text-xs font-600 text-black transition-all"
                        style={{ background: 'linear-gradient(135deg, #B8960C, #D4AF37)' }}
                      >
                        Iniciar sesión
                      </button>
                      <button
                        type="button"
                        onClick={() => switchTab('recuperar')}
                        className="flex-1 py-2 rounded-lg text-xs font-500 border border-border text-muted-foreground hover:text-foreground transition-all"
                      >
                        ¿Olvidaste tu contraseña?
                      </button>
                    </div>
                  </div>
                )}

                {/* General error */}
                {regErrorType === 'general' && regError && (
                  <div className="flex items-start gap-2.5 bg-negative/8 border border-negative/20 rounded-xl px-4 py-3">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-negative flex-shrink-0 mt-0.5">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <span className="text-sm text-negative leading-tight">{regError}</span>
                  </div>
                )}

                {/* Form fields — hidden after success */}
                {!regSuccess && (
                  <>
                    {/* Nombre completo */}
                    <div>
                      <label className="text-xs font-600 text-foreground block mb-1.5">
                        Nombre completo <span className="text-negative">*</span>
                      </label>
                      <input
                        type="text"
                        value={regNombre}
                        onChange={e => { setRegNombre(e.target.value); if (regErrors.nombre) setRegErrors(p => ({ ...p, nombre: undefined })); }}
                        placeholder="Tu nombre completo"
                        className={`w-full bg-input border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 transition-all ${regErrors.nombre ? 'border-negative/60' : 'border-border'}`}
                      />
                      {regErrors.nombre && <p className="text-xs text-negative mt-1">{regErrors.nombre}</p>}
                    </div>

                    {/* Correo */}
                    <div>
                      <label className="text-xs font-600 text-foreground block mb-1.5">
                        Correo electrónico <span className="text-negative">*</span>
                      </label>
                      <input
                        type="email"
                        value={regEmail}
                        onChange={e => { setRegEmail(e.target.value); if (regErrors.email) setRegErrors(p => ({ ...p, email: undefined })); }}
                        placeholder="tu@correo.com"
                        className={`w-full bg-input border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 transition-all ${regErrors.email ? 'border-negative/60' : 'border-border'}`}
                      />
                      {regErrors.email && <p className="text-xs text-negative mt-1">{regErrors.email}</p>}
                    </div>

                    {/* Contraseña */}
                    <div>
                      <label className="text-xs font-600 text-foreground block mb-1.5">
                        Contraseña <span className="text-negative">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={mostrarRegContrasena ? 'text' : 'password'}
                          value={regContrasena}
                          onChange={e => { setRegContrasena(e.target.value); if (regErrors.contrasena) setRegErrors(p => ({ ...p, contrasena: undefined })); }}
                          placeholder="Mínimo 6 caracteres"
                          className={`w-full bg-input border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 transition-all pr-11 ${regErrors.contrasena ? 'border-negative/60' : 'border-border'}`}
                        />
                        <button
                          type="button"
                          onClick={() => setMostrarRegContrasena(!mostrarRegContrasena)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-0.5"
                        >
                          {mostrarRegContrasena ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                              <line x1="1" y1="1" x2="23" y2="23"/>
                            </svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                              <circle cx="12" cy="12" r="3"/>
                            </svg>
                          )}
                        </button>
                      </div>
                      {regErrors.contrasena && <p className="text-xs text-negative mt-1">{regErrors.contrasena}</p>}
                    </div>

                    {/* Confirmar contraseña */}
                    <div>
                      <label className="text-xs font-600 text-foreground block mb-1.5">
                        Confirmar contraseña <span className="text-negative">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={mostrarRegConfirmar ? 'text' : 'password'}
                          value={regConfirmar}
                          onChange={e => { setRegConfirmar(e.target.value); if (regErrors.confirmar) setRegErrors(p => ({ ...p, confirmar: undefined })); }}
                          placeholder="Repite tu contraseña"
                          className={`w-full bg-input border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 transition-all pr-11 ${regErrors.confirmar ? 'border-negative/60' : 'border-border'}`}
                        />
                        <button
                          type="button"
                          onClick={() => setMostrarRegConfirmar(!mostrarRegConfirmar)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-0.5"
                        >
                          {mostrarRegConfirmar ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                              <line x1="1" y1="1" x2="23" y2="23"/>
                            </svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                              <circle cx="12" cy="12" r="3"/>
                            </svg>
                          )}
                        </button>
                      </div>
                      {regErrors.confirmar && <p className="text-xs text-negative mt-1">{regErrors.confirmar}</p>}
                    </div>

                    <button
                      type="submit"
                      disabled={regLoading}
                      className="w-full py-2.5 rounded-xl text-sm font-700 text-black transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ background: 'linear-gradient(135deg, #B8960C, #D4AF37)', boxShadow: regLoading ? 'none' : '0 4px 14px rgba(184,150,12,0.35)' }}
                    >
                      {regLoading ? (
                        <>
                          <svg className="animate-spin" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                          </svg>
                          Creando cuenta...
                        </>
                      ) : (
                        <>
                          Crear cuenta
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                            <circle cx="9" cy="7" r="4"/>
                            <line x1="19" y1="8" x2="19" y2="14"/>
                            <line x1="22" y1="11" x2="16" y2="11"/>
                          </svg>
                        </>
                      )}
                    </button>

                    {/* Back to login */}
                    <div className="pt-1 border-t border-border/50">
                      <p className="text-center text-xs text-muted-foreground pt-3">
                        ¿Ya tienes una cuenta?
                      </p>
                      <button
                        type="button"
                        onClick={() => switchTab('login')}
                        className="w-full mt-2 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-1.5"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="19" y1="12" x2="5" y2="12"/>
                          <polyline points="12 19 5 12 12 5"/>
                        </svg>
                        Iniciar sesión
                      </button>
                    </div>
                  </>
                )}
              </form>
            )}

            {/* ── RECUPERAR TAB ── */}
            {tab === 'recuperar' && (
              <form onSubmit={handleRecuperar} className="space-y-4">
                <div>
                  <label className="text-xs font-600 text-foreground block mb-1.5">Correo Electrónico</label>
                  <input
                    type="email"
                    value={correoRecuperar}
                    onChange={e => setCorreoRecuperar(e.target.value)}
                    required
                    placeholder="tu@correo.com"
                    className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 transition-all"
                  />
                </div>

                {mensajeRecuperar && (
                  <div className={`flex items-start gap-2.5 rounded-xl px-4 py-3 ${mensajeRecuperar.includes('enviado') ? 'bg-positive/8 border border-positive/20' : 'bg-info/8 border border-info/20'}`}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`flex-shrink-0 mt-0.5 ${mensajeRecuperar.includes('enviado') ? 'text-positive' : 'text-info'}`}>
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12 8 12 12 14 14"/>
                    </svg>
                    <span className={`text-sm leading-tight ${mensajeRecuperar.includes('enviado') ? 'text-positive' : 'text-info'}`}>{mensajeRecuperar}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={recuperarLoading}
                  className="w-full py-2.5 rounded-xl text-sm font-700 text-black transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: 'linear-gradient(135deg, #B8960C, #D4AF37)', boxShadow: '0 4px 14px rgba(184,150,12,0.3)' }}
                >
                  {recuperarLoading ? (
                    <>
                      <svg className="animate-spin" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                      </svg>
                      Enviando...
                    </>
                  ) : 'Enviar Enlace de Recuperación'}
                </button>

                <button
                  type="button"
                  onClick={() => switchTab('login')}
                  className="w-full py-2 text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-1.5"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="19" y1="12" x2="5" y2="12"/>
                    <polyline points="12 19 5 12 12 5"/>
                  </svg>
                  Volver al inicio de sesión
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
